import { logger } from 'module/logger';
import { SignalOccurrencesAPI } from 'api/signal-occurrences';
import { SignalSummaryValue } from 'api/signal-occurrences/types';
import moment from 'moment';
import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { fetchMonthKeysWithSignalData } from './use-timeline-data';
import {
  GroupByType,
  getPeriodKeyByOffset,
  getDateRangeForPeriod,
  getPeriodStart,
  addPeriods,
  mergePeriodData,
  findPeriodIndex,
  getPeriodKeyFromItem,
  setPeriodKeyForItem,
} from './utils';

const ITEMS_PER_BATCH = 1;
const ITEMS_ABOVE = 3;
const PERIODS_BEFORE_AFTER = 2;
const MAX_SCROLL_PERIOD_STEPS = 500;

/** Matches `getPeriodKeyByOffset` / `getPeriodStart` for WEEK (ISO week-year + ISO week). */
const formatIsoWeekPeriodKey = (m: moment.Moment): string =>
  `${m.isoWeekYear()}-W${m.isoWeek().toString().padStart(2, '0')}`;

/** ISO week keys from billing through current week, newest first (aligned with utils WEEK keys, not locale `W`). */
const getAllIsoWeekPeriodKeysFromBilling = (
  billingStartDate: string,
): string[] => {
  const keys: string[] = [];
  const defaultStartDate = moment('2020-04-01');
  const billingDate = moment(billingStartDate);
  const startDate = billingDate.isAfter(defaultStartDate)
    ? billingDate
    : defaultStartDate;

  let date = moment().clone().startOf('isoWeek');
  let iterations = 0;

  while (iterations < MAX_SCROLL_PERIOD_STEPS) {
    iterations++;
    const weekStart = date.clone().startOf('isoWeek');
    const weekEnd = date.clone().endOf('isoWeek');

    if (weekEnd.isBefore(startDate, 'day')) {
      break;
    }

    if (weekStart.isBefore(startDate, 'day')) {
      date.subtract(1, 'week');

      continue;
    }

    keys.push(formatIsoWeekPeriodKey(weekStart));
    date.subtract(1, 'week');
  }

  return keys;
};

const mergeSummaryDataWithOrderedPeriodKeys = (
  items: SignalSummaryValue[],
  orderedKeys: string[],
  groupBy: GroupByType,
): SignalSummaryValue[] => {
  const map = new Map<string, SignalSummaryValue>();

  for (const k of orderedKeys) {
    const item: SignalSummaryValue = {};

    setPeriodKeyForItem(item, k);
    map.set(k, item);
  }

  for (const item of items) {
    const k = getPeriodKeyFromItem(item, groupBy);

    if (k && map.has(k)) {
      map.set(k, item);
    }
  }

  return orderedKeys.map((k) => map.get(k)!);
};

const filterSummaryItemsByAllowedMonths = (
  merged: SignalSummaryValue[],
  allowed: Set<string> | null,
  groupBy: GroupByType,
): SignalSummaryValue[] => {
  if (groupBy !== 'MONTH' || !allowed) {
    return merged;
  }

  return merged.filter((item) =>
    allowed.has(getPeriodKeyFromItem(item, groupBy)),
  );
};

interface UseSummaryDataProps {
  companyId: number;
  groupBy: GroupByType;
  billingStartDate?: string;
  /** Passed to signal-count when set (e.g. meetings & emails). */
  view?: string;
}

export const useSummaryData = ({
  companyId,
  groupBy,
  billingStartDate = '2020-04-01',
  view,
}: UseSummaryDataProps) => {
  const [allData, setAllData] = useState<SignalSummaryValue[]>([]);
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string>('');
  const [isInitializing, setIsInitializing] = useState<boolean>(true);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const [isLoadingSelectedPeriod, setIsLoadingSelectedPeriod] =
    useState<boolean>(false);
  const [isLoadingDown, setIsLoadingDown] = useState<boolean>(false);
  const [isLoadingUp, setIsLoadingUp] = useState<boolean>(false);
  const [loadedPeriods, setLoadedPeriods] = useState<Set<string>>(new Set());
  const [monthKeysWithData, setMonthKeysWithData] = useState<
    string[] | undefined
  >(undefined);
  const fetchingPeriodsRef = useRef<Set<string>>(new Set());
  const allowedMonthKeysFilterRef = useRef<Set<string> | null>(null);

  // Get current period key based on groupBy
  const currentSystemPeriod = useMemo(() => {
    return getPeriodKeyByOffset(0, groupBy);
  }, [groupBy]);

  // temporary fix for current month
  // Helper to get date range with special handling for current month
  const getDateRangeForPeriodWithCurrentMonth = useCallback(
    (periodKey: string, groupBy: GroupByType) => {
      const dateRange = getDateRangeForPeriod(periodKey, groupBy);

      // For current month, use end of month instead of today
      if (groupBy === 'MONTH' && periodKey === currentSystemPeriod) {
        const periodDate = moment(periodKey, 'YYYY-MM');
        const endOfMonth = periodDate.clone().endOf('month');

        return {
          startDate: dateRange.startDate,
          endDate: endOfMonth.format('YYYY-MM-DD'),
        };
      }

      return dateRange;
    },
    [currentSystemPeriod],
  );

  // Reset data when groupBy or billing start changes
  useEffect(() => {
    setAllData([]);
    setLoadedPeriods(new Set());
    setSelectedPeriodKey('');
    fetchingPeriodsRef.current.clear();
    setIsInitializing(true);
    setHasInitialized(false);

    if (groupBy !== 'MONTH') {
      allowedMonthKeysFilterRef.current = null;
      setMonthKeysWithData(undefined);
    }
  }, [groupBy, billingStartDate]);

  // Initialize with current period and preload surrounding periods
  useEffect(() => {
    const initializeData = async () => {
      if (allData.length > 0 || hasInitialized) {
        setIsInitializing(false);

        return;
      }

      setIsInitializing(true);

      const isoWeekKeys =
        groupBy === 'WEEK'
          ? getAllIsoWeekPeriodKeysFromBilling(billingStartDate)
          : [];

      let allowedMonthKeys: Set<string> | null = null;

      if (groupBy === 'MONTH') {
        allowedMonthKeys = await fetchMonthKeysWithSignalData(
          companyId,
          billingStartDate,
          view,
        );
        allowedMonthKeysFilterRef.current = allowedMonthKeys;

        if (allowedMonthKeys === null) {
          setMonthKeysWithData(undefined);
        } else {
          setMonthKeysWithData(
            [...allowedMonthKeys].sort(
              (a, b) =>
                getPeriodStart(b, groupBy).valueOf() -
                getPeriodStart(a, groupBy).valueOf(),
            ),
          );
        }
      } else {
        allowedMonthKeysFilterRef.current = null;
        setMonthKeysWithData(undefined);
      }

      let currentPeriod = currentSystemPeriod;

      if (
        groupBy === 'MONTH' &&
        allowedMonthKeys &&
        allowedMonthKeys.size === 0
      ) {
        setAllData([]);
        setLoadedPeriods(new Set());
        setSelectedPeriodKey('');
        setIsInitializing(false);
        setHasInitialized(true);

        return;
      }

      if (
        groupBy === 'MONTH' &&
        allowedMonthKeys &&
        !allowedMonthKeys.has(currentPeriod)
      ) {
        const sorted = [...allowedMonthKeys].sort(
          (a, b) =>
            getPeriodStart(b, groupBy).valueOf() -
            getPeriodStart(a, groupBy).valueOf(),
        );

        currentPeriod = sorted[0] ?? currentPeriod;
      }

      setSelectedPeriodKey(currentPeriod);

      try {
        const dateRange = getDateRangeForPeriodWithCurrentMonth(
          currentPeriod,
          groupBy,
        );

        logger.info('Initializing data for current period', {
          period: currentPeriod,
          dateRange,
          groupBy,
          companyId,
        });

        const response = await SignalOccurrencesAPI.getSummary(companyId, {
          ...dateRange,
          groupBy: groupBy === 'QUARTER' ? 'MONTH' : groupBy,
        });

        const valuesWithPeriodKeys = (response.values || []).map((item) => ({
          item,
          periodKey: currentPeriod,
        }));

        let { merged } = mergePeriodData([], valuesWithPeriodKeys, groupBy);

        if (
          merged.length === 0 &&
          groupBy === 'MONTH' &&
          (!allowedMonthKeys || allowedMonthKeys.has(currentPeriod))
        ) {
          merged = mergePeriodData(
            [],
            [{ item: {} as SignalSummaryValue, periodKey: currentPeriod }],
            groupBy,
          ).merged;
        }

        merged = filterSummaryItemsByAllowedMonths(
          merged,
          allowedMonthKeys,
          groupBy,
        );

        const loadedFromInitial = new Set(
          merged
            .map((item) => getPeriodKeyFromItem(item, groupBy))
            .filter(Boolean),
        );

        let displayMerged = merged;

        if (groupBy === 'WEEK' && isoWeekKeys.length > 0) {
          displayMerged = mergeSummaryDataWithOrderedPeriodKeys(
            merged,
            isoWeekKeys,
            groupBy,
          );
        }

        // Preload surrounding periods
        const periodsToPreload: string[] = [];

        for (let i = 1; i <= ITEMS_ABOVE; i++) {
          const periodKey = getPeriodKeyByOffset(i, groupBy);

          if (
            groupBy === 'MONTH' &&
            allowedMonthKeys &&
            !allowedMonthKeys.has(periodKey)
          ) {
            continue;
          }

          const lowerCapDate = moment(billingStartDate);
          let lowerCapPeriod: string;

          switch (groupBy) {
            case 'WEEK':
              lowerCapPeriod = formatIsoWeekPeriodKey(lowerCapDate);
              break;
            case 'MONTH':
            default:
              lowerCapPeriod = lowerCapDate.format('YYYY-MM');
              break;
          }

          const periodDate = getPeriodStart(periodKey, groupBy);
          const lowerCapPeriodDate = getPeriodStart(lowerCapPeriod, groupBy);

          if (periodDate.isSameOrAfter(lowerCapPeriodDate)) {
            periodsToPreload.push(periodKey);
          }
        }

        const preloadPromises = periodsToPreload.map(async (periodKey) => {
          try {
            const periodDateRange = getDateRangeForPeriodWithCurrentMonth(
              periodKey,
              groupBy,
            );

            const preloadResponse = await SignalOccurrencesAPI.getSummary(
              companyId,
              {
                ...periodDateRange,
                groupBy: groupBy === 'QUARTER' ? 'MONTH' : groupBy,
              },
            );

            return (preloadResponse.values || []).map((item) => ({
              item,
              periodKey,
            }));
          } catch (error) {
            logger.error('Error preloading period', { periodKey, error });

            return [];
          }
        });

        const preloadResults = await Promise.all(preloadPromises);
        const allPreloadData = preloadResults.flat();

        const mergedAfterPreload =
          allPreloadData.length > 0
            ? mergePeriodData(displayMerged, allPreloadData, groupBy).merged
            : displayMerged;

        let mergedFinal = filterSummaryItemsByAllowedMonths(
          mergedAfterPreload,
          allowedMonthKeys,
          groupBy,
        );

        if (groupBy === 'WEEK' && isoWeekKeys.length > 0) {
          mergedFinal = mergeSummaryDataWithOrderedPeriodKeys(
            mergedFinal,
            isoWeekKeys,
            groupBy,
          );
        }

        const loadedKeys = new Set(loadedFromInitial);

        allPreloadData.forEach((row) => loadedKeys.add(row.periodKey));
        periodsToPreload.forEach((k) => loadedKeys.add(k));

        setAllData(mergedFinal);
        setLoadedPeriods(loadedKeys);
      } catch (error) {
        logger.error('Error initializing data', { error, groupBy, companyId });
        setAllData([]);
        setLoadedPeriods(new Set());
      } finally {
        setIsInitializing(false);
        setHasInitialized(true);
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, groupBy, view, billingStartDate]);

  // Handle period selection from TOC
  const handlePeriodSelect = useCallback(
    async (periodKey: string) => {
      if (periodKey === selectedPeriodKey) {
        return;
      }

      setSelectedPeriodKey(periodKey);

      const periodsToLoad: string[] = [];

      for (let i = -PERIODS_BEFORE_AFTER; i <= PERIODS_BEFORE_AFTER; i++) {
        const checkPeriodKey = addPeriods(periodKey, i, groupBy);

        if (
          groupBy === 'MONTH' &&
          allowedMonthKeysFilterRef.current &&
          !allowedMonthKeysFilterRef.current.has(checkPeriodKey)
        ) {
          continue;
        }

        if (findPeriodIndex(allData, checkPeriodKey, groupBy) < 0) {
          periodsToLoad.push(checkPeriodKey);
        }
      }

      if (periodsToLoad.length === 0) {
        return;
      }

      setIsLoadingSelectedPeriod(true);

      try {
        const lowerCapDate = moment(billingStartDate);
        const currentPeriod = getPeriodKeyByOffset(0, groupBy);

        let lowerCapPeriod: string;

        switch (groupBy) {
          case 'WEEK':
            lowerCapPeriod = formatIsoWeekPeriodKey(lowerCapDate);
            break;
          case 'MONTH':
          default:
            lowerCapPeriod = lowerCapDate.format('YYYY-MM');
            break;
        }

        const validPeriodsToLoad = periodsToLoad.filter((p) => {
          if (
            groupBy === 'MONTH' &&
            allowedMonthKeysFilterRef.current &&
            !allowedMonthKeysFilterRef.current.has(p)
          ) {
            return false;
          }

          const periodDate = getPeriodStart(p, groupBy);
          const lowerCapPeriodDate = getPeriodStart(lowerCapPeriod, groupBy);
          const currentPeriodDate = getPeriodStart(currentPeriod, groupBy);

          return (
            periodDate.isSameOrAfter(lowerCapPeriodDate) &&
            periodDate.isSameOrBefore(currentPeriodDate)
          );
        });

        const fetchPromises = validPeriodsToLoad.map(async (periodKey) => {
          try {
            const dateRange = getDateRangeForPeriodWithCurrentMonth(
              periodKey,
              groupBy,
            );

            const response = await SignalOccurrencesAPI.getSummary(companyId, {
              ...dateRange,
              groupBy: groupBy === 'QUARTER' ? 'MONTH' : groupBy,
            });

            return (response.values || []).map((item) => ({
              item,
              periodKey,
            }));
          } catch (error) {
            logger.error('Error loading period', { periodKey, error });

            return [];
          }
        });

        const results = await Promise.all(fetchPromises);
        const allNewData = results.flat();

        let mergedResult: SignalSummaryValue[];

        if (allNewData.length > 0) {
          mergedResult = mergePeriodData(allData, allNewData, groupBy).merged;
        } else {
          const placeholders = validPeriodsToLoad.map((pk) => ({
            item: {} as SignalSummaryValue,
            periodKey: pk,
          }));

          mergedResult =
            placeholders.length > 0
              ? mergePeriodData(allData, placeholders, groupBy).merged
              : [...allData];
        }

        mergedResult = filterSummaryItemsByAllowedMonths(
          mergedResult,
          allowedMonthKeysFilterRef.current,
          groupBy,
        );

        if (groupBy === 'WEEK') {
          const iso = getAllIsoWeekPeriodKeysFromBilling(billingStartDate);

          if (iso.length > 0) {
            mergedResult = mergeSummaryDataWithOrderedPeriodKeys(
              mergedResult,
              iso,
              groupBy,
            );
          }
        }

        setAllData(mergedResult);

        const fetchedKeys = new Set<string>();

        allNewData.forEach((row) => fetchedKeys.add(row.periodKey));
        validPeriodsToLoad.forEach((k) => fetchedKeys.add(k));
        setLoadedPeriods((prev) => new Set([...prev, ...fetchedKeys]));
      } catch (error) {
        logger.error('Error loading selected period', { error });
      } finally {
        setIsLoadingSelectedPeriod(false);
      }
    },
    [
      allData,
      selectedPeriodKey,
      companyId,
      billingStartDate,
      groupBy,
      getDateRangeForPeriodWithCurrentMonth,
    ],
  );

  // Load earlier periods (scroll down)
  const loadEarlierPeriods = useCallback(async () => {
    if (isLoadingDown || allData.length === 0) {
      return;
    }

    const oldestItem = allData[allData.length - 1];
    const oldestPeriodKey = getPeriodKeyFromItem(oldestItem, groupBy);
    const oldestPeriodDate = getPeriodStart(oldestPeriodKey, groupBy);
    const lowerCapDate = moment(billingStartDate);

    let lowerCapPeriod: string;

    switch (groupBy) {
      case 'WEEK':
        lowerCapPeriod = formatIsoWeekPeriodKey(lowerCapDate);
        break;
      case 'MONTH':
      default:
        lowerCapPeriod = lowerCapDate.format('YYYY-MM');
        break;
    }

    const lowerCapPeriodDate = getPeriodStart(lowerCapPeriod, groupBy);

    if (oldestPeriodDate.isSameOrBefore(lowerCapPeriodDate)) {
      return;
    }

    const periodsToFetch: string[] = [];
    let earlierOffset = 0;

    while (
      periodsToFetch.length < ITEMS_PER_BATCH &&
      earlierOffset < MAX_SCROLL_PERIOD_STEPS
    ) {
      earlierOffset += 1;
      const candidatePeriodKey = addPeriods(
        oldestPeriodKey,
        -earlierOffset,
        groupBy,
      );
      const candidatePeriodDate = getPeriodStart(candidatePeriodKey, groupBy);

      if (candidatePeriodDate.isBefore(lowerCapPeriodDate)) {
        break;
      }

      if (candidatePeriodDate.isSame(lowerCapPeriodDate)) {
        if (
          !loadedPeriods.has(candidatePeriodKey) &&
          !fetchingPeriodsRef.current.has(candidatePeriodKey)
        ) {
          periodsToFetch.push(candidatePeriodKey);
          fetchingPeriodsRef.current.add(candidatePeriodKey);
        }
        break;
      }

      if (
        groupBy === 'MONTH' &&
        allowedMonthKeysFilterRef.current &&
        !allowedMonthKeysFilterRef.current.has(candidatePeriodKey)
      ) {
        continue;
      }

      if (
        loadedPeriods.has(candidatePeriodKey) ||
        fetchingPeriodsRef.current.has(candidatePeriodKey)
      ) {
        continue;
      }

      periodsToFetch.push(candidatePeriodKey);
      fetchingPeriodsRef.current.add(candidatePeriodKey);
    }

    if (periodsToFetch.length === 0) {
      return;
    }

    setIsLoadingDown(true);

    try {
      const fetchPromises = periodsToFetch.map(async (periodKey) => {
        try {
          const dateRange = getDateRangeForPeriodWithCurrentMonth(
            periodKey,
            groupBy,
          );

          const response = await SignalOccurrencesAPI.getSummary(companyId, {
            ...dateRange,
            groupBy: groupBy === 'QUARTER' ? 'MONTH' : groupBy,
          });

          const rows = (response.values || []).map((item) => ({
            item,
            periodKey,
          }));

          return rows.length > 0
            ? rows
            : [{ item: {} as SignalSummaryValue, periodKey }];
        } catch (error) {
          logger.error('Error loading earlier period', { periodKey, error });

          return [];
        }
      });

      const results = await Promise.all(fetchPromises);
      const allNewData = results.flat();

      if (allNewData.length > 0) {
        let merged = mergePeriodData(allData, allNewData, groupBy).merged;

        merged = filterSummaryItemsByAllowedMonths(
          merged,
          allowedMonthKeysFilterRef.current,
          groupBy,
        );

        if (groupBy === 'WEEK') {
          const iso = getAllIsoWeekPeriodKeysFromBilling(billingStartDate);

          if (iso.length > 0) {
            merged = mergeSummaryDataWithOrderedPeriodKeys(
              merged,
              iso,
              groupBy,
            );
          }
        }

        setAllData(merged);
        setLoadedPeriods(
          new Set([
            ...loadedPeriods,
            ...periodsToFetch,
            ...allNewData.map((row) => row.periodKey),
          ]),
        );
      } else {
        const placeholders = periodsToFetch.map((pk) => ({
          item: {} as SignalSummaryValue,
          periodKey: pk,
        }));
        let merged =
          placeholders.length > 0
            ? mergePeriodData(allData, placeholders, groupBy).merged
            : [...allData];

        merged = filterSummaryItemsByAllowedMonths(
          merged,
          allowedMonthKeysFilterRef.current,
          groupBy,
        );

        if (groupBy === 'WEEK') {
          const iso = getAllIsoWeekPeriodKeysFromBilling(billingStartDate);

          if (iso.length > 0) {
            merged = mergeSummaryDataWithOrderedPeriodKeys(
              merged,
              iso,
              groupBy,
            );
          }
        }

        setAllData(merged);
        setLoadedPeriods(new Set([...loadedPeriods, ...periodsToFetch]));
      }
    } catch (error) {
      logger.error('Error loading earlier periods', { error });
    } finally {
      setIsLoadingDown(false);
      periodsToFetch.forEach((key) => fetchingPeriodsRef.current.delete(key));
    }
  }, [
    allData,
    isLoadingDown,
    loadedPeriods,
    companyId,
    billingStartDate,
    groupBy,
    getDateRangeForPeriodWithCurrentMonth,
  ]);

  // Load later periods (scroll up)
  const loadLaterPeriods = useCallback(async () => {
    if (isLoadingUp || allData.length === 0) {
      return;
    }

    const newestItem = allData[0];
    const newestPeriodKey = getPeriodKeyFromItem(newestItem, groupBy);
    const newestPeriodDate = getPeriodStart(newestPeriodKey, groupBy);
    const currentPeriod = getPeriodKeyByOffset(0, groupBy);
    const currentPeriodDate = getPeriodStart(currentPeriod, groupBy);

    if (newestPeriodDate.isSameOrAfter(currentPeriodDate)) {
      return;
    }

    const periodsToFetch: string[] = [];
    let laterOffset = 0;

    while (
      periodsToFetch.length < ITEMS_PER_BATCH &&
      laterOffset < MAX_SCROLL_PERIOD_STEPS
    ) {
      laterOffset += 1;
      const candidatePeriodKey = addPeriods(
        newestPeriodKey,
        laterOffset,
        groupBy,
      );
      const candidatePeriodDate = getPeriodStart(candidatePeriodKey, groupBy);

      if (candidatePeriodDate.isAfter(currentPeriodDate)) {
        break;
      }

      if (
        groupBy === 'MONTH' &&
        allowedMonthKeysFilterRef.current &&
        !allowedMonthKeysFilterRef.current.has(candidatePeriodKey)
      ) {
        continue;
      }

      if (
        loadedPeriods.has(candidatePeriodKey) ||
        fetchingPeriodsRef.current.has(candidatePeriodKey)
      ) {
        continue;
      }

      periodsToFetch.push(candidatePeriodKey);
      fetchingPeriodsRef.current.add(candidatePeriodKey);
    }

    if (periodsToFetch.length === 0) {
      return;
    }

    setIsLoadingUp(true);

    try {
      const fetchPromises = periodsToFetch.map(async (periodKey) => {
        try {
          const dateRange = getDateRangeForPeriodWithCurrentMonth(
            periodKey,
            groupBy,
          );

          const response = await SignalOccurrencesAPI.getSummary(companyId, {
            ...dateRange,
            groupBy: groupBy === 'QUARTER' ? 'MONTH' : groupBy,
          });

          const rows = (response.values || []).map((item) => ({
            item,
            periodKey,
          }));

          return rows.length > 0
            ? rows
            : [{ item: {} as SignalSummaryValue, periodKey }];
        } catch (error) {
          logger.error('Error loading later period', { periodKey, error });

          return [];
        }
      });

      const results = await Promise.all(fetchPromises);
      const allNewData = results.flat();

      if (allNewData.length > 0) {
        let merged = mergePeriodData(allData, allNewData, groupBy).merged;

        merged = filterSummaryItemsByAllowedMonths(
          merged,
          allowedMonthKeysFilterRef.current,
          groupBy,
        );

        if (groupBy === 'WEEK') {
          const iso = getAllIsoWeekPeriodKeysFromBilling(billingStartDate);

          if (iso.length > 0) {
            merged = mergeSummaryDataWithOrderedPeriodKeys(
              merged,
              iso,
              groupBy,
            );
          }
        }

        setAllData(merged);
        setLoadedPeriods(
          new Set([
            ...loadedPeriods,
            ...periodsToFetch,
            ...allNewData.map((row) => row.periodKey),
          ]),
        );
      } else {
        const placeholders = periodsToFetch.map((pk) => ({
          item: {} as SignalSummaryValue,
          periodKey: pk,
        }));
        let merged =
          placeholders.length > 0
            ? mergePeriodData(allData, placeholders, groupBy).merged
            : [...allData];

        merged = filterSummaryItemsByAllowedMonths(
          merged,
          allowedMonthKeysFilterRef.current,
          groupBy,
        );

        if (groupBy === 'WEEK') {
          const iso = getAllIsoWeekPeriodKeysFromBilling(billingStartDate);

          if (iso.length > 0) {
            merged = mergeSummaryDataWithOrderedPeriodKeys(
              merged,
              iso,
              groupBy,
            );
          }
        }

        setAllData(merged);
        setLoadedPeriods(new Set([...loadedPeriods, ...periodsToFetch]));
      }
    } catch (error) {
      logger.error('Error loading later periods', { error });
    } finally {
      setIsLoadingUp(false);
      periodsToFetch.forEach((key) => fetchingPeriodsRef.current.delete(key));
    }
  }, [
    allData,
    isLoadingUp,
    loadedPeriods,
    companyId,
    groupBy,
    getDateRangeForPeriodWithCurrentMonth,
    billingStartDate,
  ]);

  return {
    data: allData,
    selectedPeriodKey,
    isInitializing,
    isLoadingSelectedPeriod,
    isLoadingDown,
    isLoadingUp,
    handlePeriodSelect,
    loadEarlierPeriods,
    loadLaterPeriods,
    monthKeysWithData,
  };
};
